import type { Pose } from './localEdits';

type Vec = { x: number; y: number; z: number };

// The renderer reports poses rounded to two decimals, so a pose it left alone differs by at most half a step;
// anything further is a value it reported as moved
const ROUNDING = 0.0055;
// Euler angles this far apart on an axis are the same rotation written another way (the renderer's are 0 to 360)
const OTHER_TRIPLE_DEGREES = 1;
const ROTATION_TOLERANCE_DEGREES = 0.01;

const near = (a?: Vec, b?: Vec) =>
  !!a && !!b && Math.abs(a.x - b.x) <= ROUNDING && Math.abs(a.y - b.y) <= ROUNDING && Math.abs(a.z - b.z) <= ROUNDING;

const angleApart = (a: number, b: number) => Math.abs(((((a - b) % 360) + 540) % 360) - 180);

// Unity's Euler order: z, then x, then y
function quaternion({ x, y, z }: Vec): [number, number, number, number] {
  const h = Math.PI / 360;
  const [cx, sx, cy, sy, cz, sz] = [Math.cos(x * h), Math.sin(x * h), Math.cos(y * h), Math.sin(y * h), Math.cos(z * h), Math.sin(z * h)];
  return [cy * sx * cz + sy * cx * sz, sy * cx * cz - cy * sx * sz, cy * cx * sz - sy * sx * cz, cy * cx * cz + sy * sx * sz];
}

function sameRotation(a?: Vec, b?: Vec): boolean {
  if (!a || !b) return false;
  const apart = [angleApart(a.x, b.x), angleApart(a.y, b.y), angleApart(a.z, b.z)];
  if (apart.every(d => d < OTHER_TRIPLE_DEGREES)) return apart.every(d => d <= ROUNDING);
  const p = quaternion(a);
  const q = quaternion(b);
  const dot = Math.abs(p[0] * q[0] + p[1] * q[1] + p[2] * q[2] + p[3] * q[3]);
  return dot >= Math.cos((ROTATION_TOLERANCE_DEGREES * Math.PI) / 360);
}

export const samePose = (a: Pose, b: Pose): boolean =>
  near(a.position, b.position) && near(a.scale, b.scale) && sameRotation(a.rotation, b.rotation);
